import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {JsPDFUtils} from "./JsPDFUtils";

export class GraphPDFUtils {
  static async timeout(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  private static async loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  private static async fetchSvgString(src: string): Promise<string | null> {
    const urls = src.includes("?") ? [src] : [`${src}?get`, src];

    for (const url of urls) {
      try {
        const response = await fetch(url);
        const svgString = await response.text();

        if (svgString.includes("<svg")) {
          return svgString;
        }
      } catch (e) {
        console.warn("Could not fetch diagram SVG", e);
      }
    }

    return null;
  }

  private static resizeSvg(svgString: string, width: number, height: number): string {
    return svgString.replace(/<svg\b([^>]*)>/, (_match, attributes) => {
      const cleanAttributes = attributes.replace(/\s(width|height)="[^"]*"/g, "");
      return `<svg${cleanAttributes} width="${width}" height="${height}">`;
    });
  }

  private static async imageToDataURL(image: HTMLImageElement): Promise<string | null> {
    if (image.src.startsWith("data:")) {
      return image.src;
    }

    const svgString = await this.fetchSvgString(image.src);
    if (svgString) {
      const width = image.width || image.naturalWidth || 80;
      const height = image.height || image.naturalHeight || 80;
      const resizedSvgString = this.resizeSvg(svgString.replace(/\r?\n|\r/g, '').trim(), width, height)
      const decodedSvgString = unescape(encodeURIComponent(resizedSvgString))
      const base64 = btoa(decodedSvgString)
      const imageSource = `data:image/svg+xml;base64,${base64}`
      const png = await this.base64SvgToBase64Png(imageSource, 200)

      return typeof png === "string" ? png : null;
    }

    try {
      const response = await fetch(image.src);
      const blob = await response.blob();

      return await new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : null);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn("Could not inline legend image", e);
      return null;
    }
  }

  private static async inlineImagesForCapture(container: HTMLElement): Promise<() => void> {
    const images = Array.from(container.getElementsByTagName("img"));
    const originals = images.map((image) => ({ image, src: image.src }));

    await Promise.all(images.map(async (image) => {
      const dataURL = await this.imageToDataURL(image);
      if (dataURL) {
        image.src = dataURL;
      }
    }));

    await Promise.all(images.map((image) => {
      if (image.complete) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve) => {
        image.onload = () => resolve();
        image.onerror = () => resolve();
      });
    }));

    return () => originals.forEach(({ image, src }) => {
      image.src = src;
    });
  }

  private static async addLegendToPDF(pdf: jsPDF, offset: number, pageHeight: number) {
    const legendHTML = document.querySelector(".legend-container") as HTMLElement | null;
    if (!legendHTML) {
      return;
    }

    if (offset > pageHeight - 110) {
      pdf.addPage();
      offset = 40;
    }

    const restoreImages = await this.inlineImagesForCapture(legendHTML);

    try {
      const legendCanvas = await html2canvas(legendHTML, { backgroundColor: "#ffffff", useCORS: true });
      const width = 500;
      const height = legendCanvas.height * width / legendCanvas.width;
      pdf.addImage(legendCanvas.toDataURL("image/png"), "PNG", 40, offset, width, height);
    } finally {
      restoreImages();
    }
  }

  static async exportGraphPDF(appTitle: string,
    graphTitle: string,
    graphComparisonTitle: string,
    diagramTitle: string,
    appLogo: string,
    bbriLogo : string) {

    JsPDFUtils.initJsPDF();

    const graphHTML = document.getElementById("graph-zone")
    const tabHTML = document.getElementById("graph-table")
    const resultHTML = document.getElementById("graph-result")
    const diagramHTML = document.getElementById("diagram-zone")

    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageHeight = pdf.internal.pageSize.getHeight();

    const applicationTitle = appTitle
    const title = graphTitle

    let offset = 0

    try {
      const langImage = await this.loadImage(appLogo);
      const logoImageRapport = langImage.width > langImage.height ? langImage.width / langImage.height : langImage.height / langImage.width;
      let width = 0
      if (logoImageRapport > 2) {
        width = 30 * logoImageRapport
      }
      else {
        width = 30 / logoImageRapport
      }
      pdf.addImage(langImage, 'png', 40, 20, width, 30);
    } catch (e) {
      console.warn("Could not load appLogo", e);
    }

    try {
      const bbriImage = await this.loadImage(bbriLogo);
      const bbriImageRapport = bbriImage.width > bbriImage.height ? bbriImage.width / bbriImage.height : bbriImage.height / bbriImage.width;
      let widthBbri = 0
      if (bbriImageRapport > 2) {
        widthBbri = 30 * bbriImageRapport
      }
      else {
        widthBbri = 30 / bbriImageRapport
      }
      pdf.addImage(bbriImage, 'png', 420, 20, widthBbri, 30);
    } catch (e) {
      console.warn("Could not load bbriLogo", e);
    }

    // pdf.setFontSize(25)
    // pdf.text(applicationTitle, 40, 30 + offset)
    offset += 60
    pdf.setFontSize(18)
    pdf.setFont("Roboto-Bold", "normal")
    pdf.text(title, 40, 30 + offset)
    offset += 20
    pdf.setFont("Roboto-Regular", "normal")

    if (resultHTML) {
      pdf.setFontSize(12)
      const resultLines = pdf.splitTextToSize(resultHTML.innerText, 500)
      pdf.text(resultLines, 40, 30 + offset)
      offset += resultLines.length * 14 + 20
    }

    if (graphHTML) {
      graphHTML.classList.add("print-graph")
      await this.timeout(50)
      let canvasGraph = await html2canvas(graphHTML)
      pdf.addImage(canvasGraph.toDataURL("image/jpeg"), "JPEG", 40, 50 + offset, 480, 220)
      offset += 280
      graphHTML.classList.remove("print-graph")
    }
    if (tabHTML) {
      const tabCanvas = await html2canvas(tabHTML)
      let imgWidth = 210;
      let pageHeight = 200;
      let imgHeight = tabCanvas.height * imgWidth / tabCanvas.width;
      let heightLeft = imgHeight;

      let position = 0

      pdf.setFontSize(18)
      pdf.setFont("Roboto-Bold", "normal")
      pdf.text(graphComparisonTitle, 40, 20 + offset)
      pdf.addImage(tabCanvas.toDataURL("image/jpeg"), "JPEG", 50, 50 + offset, 480, tabCanvas.height/3)
      heightLeft -= pageHeight

      if (heightLeft >= 0) {
        position = heightLeft - imgHeight
        console.log(position)
        pdf.addPage()
        pdf.addImage(tabCanvas.toDataURL("image/jpeg"), "JPEG", 50, position - 235, 480, tabCanvas.height/3)
      }
      offset += 40
    }
    if (diagramHTML) {
      if (graphHTML || offset > pageHeight - 420) {
        pdf.addPage()
        offset = 20
      }

      pdf.setFontSize(18)
      pdf.setFont("Roboto-Bold", "normal")
      pdf.text(diagramTitle, 40, 30 + offset, { maxWidth: 500})
      offset += 40

      const diagramImage = diagramHTML.getElementsByTagName("img")[0]
      if (diagramImage) {
        const svgString = await this.fetchSvgString(diagramImage.src)

        if (svgString) {
          const width = diagramImage.width || diagramImage.naturalWidth || 1900
          const height = diagramImage.height || diagramImage.naturalHeight || 1000
          const resizedSvgString = this.resizeSvg(svgString.replace(/\r?\n|\r/g, '').trim(), width, height)
          const decodedSvgString = unescape(encodeURIComponent(resizedSvgString))
          const base64 = btoa(decodedSvgString)
          const imageSource = `data:image/svg+xml;base64,${base64}`
          const diagramPNG = await this.base64SvgToBase64Png(imageSource, 1900)

          if (diagramPNG) {
            pdf.addImage(diagramPNG as HTMLImageElement, "PNG", 40, offset, 500, 360)
            offset += 390
          }
        }
      }

      await this.addLegendToPDF(pdf, offset, pageHeight)
    }

    pdf.save("pv-diagram.pdf", { returnPromise: true })
      .then(() => {
        if (graphHTML) {
          graphHTML.classList.remove("print-graph")
        }
      })

  }

  private static base64SvgToBase64Png (originalBase64, width) {
    return new Promise(resolve => {
      let img = document.createElement('img');
      img.onload = function () {
        document.body.appendChild(img);
        let canvas = document.createElement("canvas");
        let ratio = (img.clientWidth / img.clientHeight) || 1;
        document.body.removeChild(img);
        canvas.width = width;
        canvas.height = width / ratio;
        let ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          try {
            let data = canvas.toDataURL('image/png');
            resolve(data);
          } catch (e) {
            resolve(null);
          }
        }
        else {
          resolve(null)
        }
      };
      img.onerror = function() {
        resolve(null);
      };
      img.src = originalBase64;
    });
  }
}
